import { type JSX, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { MobiLauncherConfig as Config } from '@schema/index.js';

/**
 * Default preview URL — explicit override via VITE_PREVIEW_URL still wins,
 * but otherwise we pick the dev-server URL in dev and the production
 * preview app in prod. Avoids needing to set a Pages env var per deploy
 * AND keeps `npm run dev` working without any config.
 */
const PREVIEW_URL: string =
  (import.meta.env['VITE_PREVIEW_URL'] as string | undefined) ??
  (import.meta.env.PROD
    ? 'https://app.mobilauncher.com/preview'
    : 'http://localhost:5173/preview.html');

/**
 * A common Android enterprise device. Numbers are the physical screen
 * resolution; the bezel + status-bar are decorative chrome around the
 * iframe. `kind` toggles the bezel silhouette (tablet rectangle vs.
 * handheld rounded rectangle).
 */
interface DeviceSpec {
  id: string;
  label: string;
  /** Physical pixel resolution (matches vendor spec sheets). */
  width: number;
  height: number;
  /**
   * Device pixel ratio. Used to derive the logical (CSS) viewport that
   * the iframe is sized to — that's the viewport real on-device Chrome
   * sees, so media queries inside the launcher fire correctly. Without
   * this the iframe was sized to physical px (e.g., 1080×2400 for a
   * TC73), so a 540px breakpoint never triggered even on devices that'd
   * absolutely hit it in the real world (TC73 logical width = 360 at
   * DPR 3).
   */
  dpr: number;
  /** Decorative bezel thickness on all sides, in CSS px (pre-scale). */
  bezel: number;
  /** Visible Android status bar height, in CSS px (pre-scale). */
  statusBar: number;
  kind: 'tablet' | 'handheld';
}

/**
 * Resolutions verified against vendor spec sheets. Handhelds are stored
 * in their natural portrait orientation; the orientation toggle in the
 * preview UI flips them landscape on demand. Rugged bezels (40+) reflect
 * actual device industrial design — these aren't slim consumer phones.
 */
const DEVICES: readonly DeviceSpec[] = [
  // Workhorse rugged tablets — common across enterprise fleets.
  { id: 'zebra-et40',            label: 'Zebra ET40 — 10.1″',              width: 1920, height: 1200, dpr: 1.5, bezel: 40, statusBar: 28, kind: 'tablet' },
  { id: 'galaxy-tab-active-pro', label: 'Galaxy Tab Active Pro — 10.1″',   width: 1920, height: 1200, dpr: 1.5, bezel: 44, statusBar: 28, kind: 'tablet' },
  { id: 'galaxy-tab-active5',    label: 'Galaxy Tab Active5 — 8.0″',       width: 1920, height: 1200, dpr: 1.5, bezel: 38, statusBar: 28, kind: 'tablet' },
  { id: 'honeywell-eda10a',      label: 'Honeywell EDA10A — 10.1″',        width: 1920, height: 1200, dpr: 1.5, bezel: 40, statusBar: 28, kind: 'tablet' },
  // Rugged handhelds in portrait natural orientation.
  { id: 'zebra-tc73',            label: 'Zebra TC73 — 6.0″',               width: 1080, height: 2400, dpr: 3,   bezel: 40, statusBar: 36, kind: 'handheld' },
  { id: 'zebra-tc22',            label: 'Zebra TC22 — 5.5″',               width:  720, height: 1440, dpr: 2,   bezel: 42, statusBar: 32, kind: 'handheld' },
  { id: 'zebra-tc52',            label: 'Zebra TC52 — 5.0″',               width:  720, height: 1280, dpr: 2,   bezel: 42, statusBar: 30, kind: 'handheld' },
  { id: 'honeywell-ct45',        label: 'Honeywell CT45 — 5.0″',           width:  720, height: 1280, dpr: 2,   bezel: 40, statusBar: 30, kind: 'handheld' },
  { id: 'honeywell-ct40',        label: 'Honeywell CT40 — 5.0″',           width:  720, height: 1280, dpr: 2,   bezel: 42, statusBar: 30, kind: 'handheld' },
  { id: 'honeywell-ck65',        label: 'Honeywell CK65 — 4.7″ (keypad)',  width: 1080, height: 1920, dpr: 3,   bezel: 44, statusBar: 36, kind: 'handheld' },
  { id: 'samsung-xcover7',       label: 'Galaxy XCover7 — 6.6″',           width: 1080, height: 2340, dpr: 3,   bezel: 38, statusBar: 34, kind: 'handheld' },
  { id: 'samsung-xcover6-pro',   label: 'Galaxy XCover6 Pro — 6.6″',       width: 1080, height: 2408, dpr: 3,   bezel: 38, statusBar: 34, kind: 'handheld' },
];

export interface LivePreviewProps {
  config: Config;
  /**
   * Fires once when the iframe has loaded AND sent `preview-ready`.
   * BuilderEditor uses this to gate the first-time editor tour so it
   * doesn't fire against an empty iframe.
   */
  onReady?: () => void;
}

/**
 * Iframe-hosted live preview of the launcher with the in-progress config.
 *
 * Why iframe: the renderer's CSS is full-bleed (locks html/body to 100%
 * height and overflow:hidden) — embedding it in-process would clash with
 * the builder's layout. Iframe gives a clean visual sandbox AND lets us
 * point at the renderer's actual production bundle in deployment, so
 * what the builder shows IS what ships on-device.
 *
 * Postmessage protocol:
 *   builder → iframe: { type: 'set-config', config: MobiLauncherConfig }
 *   iframe  → builder: { type: 'preview-ready' }   (on load)
 *
 * Config sends are debounced 200ms so typing into a text field doesn't
 * fire a render-pass per keystroke. The iframe's onLoad handler also
 * sends current config so reloads pick up immediately.
 *
 * Device bezel: a dropdown picks one of the common Android enterprise
 * devices; the iframe is then sized to its physical resolution and
 * uniform-scaled to fit the preview pane. A mock Android status bar
 * sits inside the bezel so the on-device look is preserved.
 */
type Orientation = 'landscape' | 'portrait';

/** Natural orientation derived from the device's spec dimensions. */
function naturalOrientation(d: DeviceSpec): Orientation {
  return d.width >= d.height ? 'landscape' : 'portrait';
}

/** Apply orientation to a device spec, swapping w/h when flipped. */
function oriented(d: DeviceSpec, o: Orientation): { width: number; height: number } {
  const natural = naturalOrientation(d);
  if (o === natural) return { width: d.width, height: d.height };
  return { width: d.height, height: d.width };
}

/**
 * Logical (CSS) pixel size — physical dimensions divided by DPR. This is
 * the viewport size real on-device Chrome reports to its content, so it's
 * what we want for the iframe so media queries inside the launcher fire
 * the same way they will in production.
 */
function logicalSize(d: DeviceSpec, o: Orientation): { width: number; height: number } {
  const phys = oriented(d, o);
  return { width: phys.width / d.dpr, height: phys.height / d.dpr };
}

/**
 * How the device shell sizes relative to the preview pane.
 *
 *   'fit'        — scale uniformly to fit BOTH width and height (default).
 *                  Whole device always visible, no scroll.
 *   'fill-width' — scale to fit WIDTH only. Useful for portrait handhelds
 *                  on short panes — the launcher's tile grid renders at
 *                  a usable size and the user scrolls vertically to see
 *                  the rest of the screen.
 */
type FitMode = 'fit' | 'fill-width';

export function LivePreview({ config, onReady }: LivePreviewProps): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string>('fit');
  const [orientation, setOrientation] = useState<Orientation>('landscape');
  const [fitMode, setFitMode] = useState<FitMode>('fit');
  const [shotStatus, setShotStatus] = useState<
    'idle' | 'capturing' | 'unsupported' | 'failed'
  >('idle');
  /**
   * When true, the saved PNG includes the device bezel + status bar
   * around the captured launcher content. When false, just the
   * launcher screen itself (useful for embedding in docs / Slack
   * without the chrome). Forced to false when no device is selected
   * — there's no frame to include.
   */
  const [includeFrame, setIncludeFrame] = useState<boolean>(true);

  const device = useMemo(
    () => DEVICES.find((d) => d.id === deviceId) ?? null,
    [deviceId],
  );

  // When the user picks a new device, snap to its natural orientation so
  // a handheld doesn't default to landscape (looks weird and crops the
  // launcher's tile grid awkwardly).
  function pickDevice(id: string): void {
    setDeviceId(id);
    const next = DEVICES.find((d) => d.id === id);
    if (next) setOrientation(naturalOrientation(next));
  }

  /**
   * Snapshot of "what does the user want done with the next screenshot
   * response?" set at request time and consumed when the iframe posts
   * back the data: URL. We can't read live state from the handler (it'd
   * be stale on closure) so we stash the choice here.
   */
  const pendingCaptureRef = useRef<
    { withFrame: boolean; device: DeviceSpec | null; orientation: Orientation } | null
  >(null);

  /**
   * Live ref to the current config so message handlers can read the
   * latest without re-running their effects on every keystroke.
   */
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Inbound iframe → parent messages.
  //   'preview-ready'  — iframe's message listener is attached. We re-post
  //                       the current config now to win the race against
  //                       the onLoad-time post (which can fire BEFORE the
  //                       iframe's React useEffect registers its listener,
  //                       silently dropping the first set-config).
  //   'screenshot'     — response to our request-screenshot ping, carries
  //                       a data: URL (PNG) the user can save
  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      const data = e.data as { type?: string; dataUrl?: unknown } | null;
      if (data?.type === 'preview-ready') {
        setReady(true);
        onReady?.();
        // Re-send the current config now that we know the listener is up.
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'set-config', config: configRef.current },
          '*',
        );
        return;
      }
      if (data?.type === 'screenshot' && typeof data.dataUrl === 'string') {
        const pending = pendingCaptureRef.current;
        pendingCaptureRef.current = null;
        const filename = suggestedFilename(configRef.current.deploymentName);
        if (pending && pending.withFrame && pending.device !== null) {
          void composeWithDeviceFrame(data.dataUrl, pending.device, pending.orientation)
            .then((composedUrl) => {
              downloadDataUrl(composedUrl, filename);
              setShotStatus('idle');
            })
            .catch(() => {
              // Compose failed — fall back to the raw iframe shot so
              // the user at least gets *something*.
              downloadDataUrl(data.dataUrl as string, filename);
              setShotStatus('idle');
            });
        } else {
          downloadDataUrl(data.dataUrl, filename);
          setShotStatus('idle');
        }
        return;
      }
      if (data?.type === 'screenshot-error') {
        pendingCaptureRef.current = null;
        setShotStatus('failed');
        return;
      }
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
    // configRef is read inside, but it's a ref so we don't depend on it.
  }, []);

  function requestScreenshot(): void {
    if (iframeRef.current?.contentWindow === undefined || iframeRef.current?.contentWindow === null) {
      setShotStatus('unsupported');
      return;
    }
    pendingCaptureRef.current = {
      withFrame: includeFrame && device !== null,
      device,
      orientation,
    };
    setShotStatus('capturing');
    iframeRef.current.contentWindow.postMessage({ type: 'request-screenshot' }, '*');
    // Time out after 8 seconds so the button doesn't stay stuck if the
    // iframe is running an older bundle that doesn't know the message.
    window.setTimeout(() => {
      setShotStatus((s) => (s === 'capturing' ? 'unsupported' : s));
    }, 8000);
  }

  // Debounced config postmessage on every config change.
  useEffect(() => {
    const handle = setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'set-config', config },
        '*',
      );
    }, 200);
    return () => {
      clearTimeout(handle);
    };
  }, [config]);

  function onLoad(): void {
    setIframeError(null);
    // Send current config immediately on load — the 'preview-ready' race
    // means the listener may not be attached yet when our useEffect fires
    // first time, so post both ways.
    iframeRef.current?.contentWindow?.postMessage({ type: 'set-config', config }, '*');
  }
  function onError(): void {
    setIframeError(
      import.meta.env.PROD
        ? `Preview iframe failed to load (${PREVIEW_URL}). If you blocked third-party iframes or have an ad-blocker, that's the likely cause.`
        : `Preview iframe failed to load. Make sure the renderer dev server is running at ${PREVIEW_URL}.`,
    );
  }

  return (
    <div className="live-preview">
      <div className="live-preview__chrome">
        <label className="live-preview__device-picker">
          <span className="live-preview__device-label">Device</span>
          <select
            className="live-preview__device-select"
            value={deviceId}
            onChange={(e) => pickDevice(e.target.value)}
            data-testid="preview-device-select"
          >
            <option value="fit">Fit pane (no bezel)</option>
            {DEVICES.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label} — {d.width}×{d.height}
              </option>
            ))}
          </select>
        </label>

        {device !== null && (
          <>
            <div
              className="live-preview__orientation"
              role="radiogroup"
              aria-label="Orientation"
            >
              <OrientationButton
                active={orientation === 'landscape'}
                onClick={() => setOrientation('landscape')}
                title="Landscape"
              >
                <span aria-hidden>▭</span>
                <span className="visually-hidden">Landscape</span>
              </OrientationButton>
              <OrientationButton
                active={orientation === 'portrait'}
                onClick={() => setOrientation('portrait')}
                title="Portrait"
              >
                <span aria-hidden>▯</span>
                <span className="visually-hidden">Portrait</span>
              </OrientationButton>
            </div>
            <div
              className="live-preview__fitmode"
              role="radiogroup"
              aria-label="Fit mode"
            >
              <OrientationButton
                active={fitMode === 'fit'}
                onClick={() => setFitMode('fit')}
                title="Fit — scale to show the whole device"
              >
                Fit
              </OrientationButton>
              <OrientationButton
                active={fitMode === 'fill-width'}
                onClick={() => setFitMode('fill-width')}
                title="Fill width — scale to pane width; scroll vertically for taller devices"
              >
                Fill W
              </OrientationButton>
            </div>
          </>
        )}
        <div className="live-preview__save-group">
          {device !== null && (
            <label
              className="live-preview__save-opt"
              title="Include the device bezel + status bar in the saved PNG"
            >
              <input
                type="checkbox"
                checked={includeFrame}
                onChange={(e) => setIncludeFrame(e.target.checked)}
                data-testid="preview-include-frame"
              />
              <span>Frame</span>
            </label>
          )}
          <button
            type="button"
            className="live-preview__shot"
            onClick={requestScreenshot}
            disabled={shotStatus === 'capturing'}
            title={
              shotStatus === 'unsupported'
                ? 'Preview app needs an update for screenshot support'
                : 'Save a PNG of the current launcher preview'
            }
            data-testid="preview-screenshot"
          >
            {shotStatus === 'capturing'
              ? 'Capturing…'
              : shotStatus === 'unsupported'
                ? '📷 Unavailable'
                : shotStatus === 'failed'
                  ? '📷 Failed — retry'
                  : '📷 Save'}
          </button>
        </div>
      </div>

      <div className="live-preview__stage" ref={hostRef}>
        {device === null ? (
          <iframe
            ref={iframeRef}
            title="Launcher preview"
            src={PREVIEW_URL}
            className="live-preview__iframe"
            onLoad={onLoad}
            onError={onError}
            data-testid="live-preview-iframe"
          />
        ) : (
          <DeviceFrame device={device} orientation={orientation} fitMode={fitMode} hostRef={hostRef}>
            <iframe
              ref={iframeRef}
              title="Launcher preview"
              src={PREVIEW_URL}
              className="live-preview__iframe live-preview__iframe--framed"
              style={logicalSize(device, orientation)}
              onLoad={onLoad}
              onError={onError}
              data-testid="live-preview-iframe"
            />
          </DeviceFrame>
        )}
      </div>

      {iframeError !== null && (
        <div className="live-preview__overlay" role="alert">
          {iframeError}
        </div>
      )}
      {!ready && iframeError === null && (
        <div className="live-preview__overlay live-preview__overlay--hint">
          Connecting to preview…
          {!import.meta.env.PROD && (
            <p className="live-preview__hint">
              If this hangs, run <code>npm run dev</code> in the renderer
              package (port 5173).
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the bezel + Android status bar around the framed iframe. Scales
 * uniformly so the device fits the stage. ResizeObserver keeps the scale
 * fresh when the user resizes the window or toggles preview-pane width.
 */
function DeviceFrame({
  device,
  orientation,
  fitMode,
  hostRef,
  children,
}: {
  device: DeviceSpec;
  orientation: Orientation;
  fitMode: FitMode;
  hostRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}): JSX.Element {
  const { width: physicalW, height: physicalH } = oriented(device, orientation);
  // Iframe is sized to logical (CSS) px so the launcher inside sees the
  // viewport a real device's Chrome would see. Bezel + statusBar stay
  // in their original units — they're decorative chrome around the
  // screen and are unrelated to the device's DPR.
  const screenW = physicalW / device.dpr;
  const screenH = physicalH / device.dpr;
  const frameOuterWidth = screenW + device.bezel * 2;
  const frameOuterHeight = screenH + device.bezel * 2 + device.statusBar;
  const [scale, setScale] = useState(0.2);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    function recompute(): void {
      if (host === null) return;
      const padding = 24; // breathing room
      const availW = host.clientWidth - padding;
      const availH = host.clientHeight - padding;
      if (availW <= 0 || availH <= 0) return;
      // fit       — uniform scale that respects BOTH axes. Treats the
      //              effective height as max(availH, 1024) so on shorter
      //              monitors the device still renders at a usable size;
      //              the stage's overflow-y:auto absorbs the difference.
      // fill-width — scale to width only; device may overflow vertically.
      const FIT_MIN_HEIGHT = 1024;
      const s =
        fitMode === 'fill-width'
          ? Math.min(availW / frameOuterWidth, 1)
          : Math.min(
              availW / frameOuterWidth,
              Math.max(availH, FIT_MIN_HEIGHT) / frameOuterHeight,
              1,
            );
      setScale(s);
    }
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(host);
    return () => {
      ro.disconnect();
    };
  }, [hostRef, frameOuterWidth, frameOuterHeight, fitMode]);

  return (
    <div
      className="device-shell"
      style={{ width: frameOuterWidth * scale, height: frameOuterHeight * scale }}
    >
      <div
        className={`device-frame device-frame--${device.kind}`}
        style={{
          width: frameOuterWidth,
          height: frameOuterHeight,
          padding: device.bezel,
          transform: `scale(${scale})`,
        }}
      >
        <div
          className="device-frame__screen"
          style={{ width: screenW, height: screenH + device.statusBar }}
        >
          <AndroidStatusBar height={device.statusBar} width={screenW} />
          {children}
        </div>
        {device.kind === 'handheld' && (
          <span className="device-frame__speaker" aria-hidden />
        )}
      </div>
    </div>
  );
}

/**
 * Decorative Android status bar — fixed glyphs (signal/wifi/battery) plus
 * a live clock. Not interactive; it sits inside the bezel above the iframe
 * to make the preview read as an on-device view rather than a web page.
 */
function AndroidStatusBar({ height, width }: { height: number; width: number }): JSX.Element {
  const [clock, setClock] = useState(formatClock(new Date()));
  useEffect(() => {
    const h = setInterval(() => setClock(formatClock(new Date())), 30_000);
    return () => clearInterval(h);
  }, []);
  return (
    <div
      className="android-statusbar"
      style={{ height, width, fontSize: Math.max(11, Math.round(height * 0.45)) }}
      aria-hidden
    >
      <span className="android-statusbar__time">{clock}</span>
      <span className="android-statusbar__icons">
        <span className="android-statusbar__icon">▲</span>
        <span className="android-statusbar__icon">📶</span>
        <span className="android-statusbar__icon">🔋</span>
      </span>
    </div>
  );
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function OrientationButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={`live-preview__orient-btn${active ? ' live-preview__orient-btn--active' : ''}`}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/** Slug a deployment name into a safe filename stem. */
function suggestedFilename(deploymentName: string): string {
  const stamp = new Date()
    .toISOString()
    .replace(/[:T]/g, '-')
    .replace(/\..+$/, '');
  const stem =
    deploymentName
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'launcher-preview';
  return `${stem}-${stamp}.png`;
}

/** Trigger a download for a data: URL (already encoded by the iframe). */
function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Composite the iframe's launcher screenshot onto a canvas with the
 * device bezel + Android status bar drawn around it. Mirrors the
 * dimensions used by the on-screen DeviceFrame so the saved PNG looks
 * the same as what the user sees in the preview (minus the scale
 * transform — we draw at 1x logical resolution × 2× pixel ratio).
 */
async function composeWithDeviceFrame(
  screenDataUrl: string,
  device: DeviceSpec,
  orientation: Orientation,
): Promise<string> {
  const { width: physicalW, height: physicalH } = oriented(device, orientation);
  const screenW = physicalW / device.dpr;
  const screenH = physicalH / device.dpr;
  const totalW = screenW + device.bezel * 2;
  const totalH = screenH + device.bezel * 2 + device.statusBar;
  const pixelRatio = 2;

  const img = await loadImage(screenDataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(totalW * pixelRatio);
  canvas.height = Math.round(totalH * pixelRatio);
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas 2d context unavailable');
  ctx.scale(pixelRatio, pixelRatio);

  // Bezel — matches device-frame's #1a1c1f background.
  ctx.fillStyle = '#1a1c1f';
  ctx.fillRect(0, 0, totalW, totalH);
  // Subtle inner highlight to echo the on-screen shadow.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, totalW - 2, totalH - 2);

  // Android status bar across the top of the screen area.
  const sbHeight = device.statusBar;
  ctx.fillStyle = '#0b0b0d';
  ctx.fillRect(device.bezel, device.bezel, screenW, sbHeight);
  // Clock + icons. Sizes mirror AndroidStatusBar's CSS.
  const fontSize = Math.max(11, Math.round(sbHeight * 0.45));
  ctx.fillStyle = '#e8eaed';
  ctx.font = `${String(fontSize)}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(formatClockForCanvas(new Date()), device.bezel + 12, device.bezel + sbHeight / 2);
  ctx.textAlign = 'right';
  ctx.fillText('▲  📶  🔋', device.bezel + screenW - 12, device.bezel + sbHeight / 2);

  // The captured launcher content fills the remaining screen rectangle.
  ctx.drawImage(
    img,
    device.bezel,
    device.bezel + sbHeight,
    screenW,
    screenH,
  );

  return canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });
}

function formatClockForCanvas(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
